<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const transfers = [
  { ref: 'TRF-2025-0012', project: 'Solar Park Beta', amount: 500, date: '2025-03-01', recipient: 'GreenTrade Partners Ltd', recipientId: 'ACC-000234', status: 'Pending', statusColor: 'yellow' },
]
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-4xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Transfer Credits</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">Transfer Carbon Credits</h1>
            <p class="text-gray-600">Transfer carbon credits to another registered account holder on the registry. The recipient must have an active account.</p>
          </div>
        </section>

        <form class="space-y-6">
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-exchange-alt text-primary mr-3"></i>Transfer Details
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Select Issuance Batch *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select batch</option>
                  <option>CCI-2024-00089-001 — Solar Park Beta (5,840 available)</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Credits to Transfer (tCO2e) *</label>
                <input type="number" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="0" />
                <p class="text-xs text-gray-500 mt-1">Available: 5,840 tCO2e</p>
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Recipient Account ID *</label>
                <input type="text" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="e.g. ACC-000234" />
                <p class="text-xs text-gray-500 mt-1">Enter the registry account ID of the recipient. They must be a registered account holder.</p>
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Transfer Purpose / Notes</label>
                <textarea rows="3" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Optional reference or description for this transfer."></textarea>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-accent mb-4">ITMO Authorization</h2>
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p class="text-sm text-blue-800">
                <i class="fas fa-info-circle mr-2"></i>
                If this transfer involves Internationally Transferred Mitigation Outcomes (ITMOs) across national borders, host country authorization and corresponding adjustments are required before the transfer can be completed.
              </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Is this a cross-border ITMO transfer?</label>
              <div class="flex items-center space-x-4">
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input type="radio" name="itmo" value="yes" class="text-primary focus:ring-primary" />
                  <span class="text-sm text-gray-700">Yes – ITMO authorization required</span>
                </label>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input type="radio" name="itmo" value="no" class="text-primary focus:ring-primary" checked />
                  <span class="text-sm text-gray-700">No – domestic transfer</span>
                </label>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <label class="flex items-start space-x-3 cursor-pointer">
              <input type="checkbox" class="mt-1 rounded border-gray-300 text-primary focus:ring-primary" />
              <span class="text-sm text-gray-700">I confirm the recipient account details are correct. I understand that submitted transfers cannot be cancelled once processed.</span>
            </label>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <button type="button" class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" class="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90 flex items-center">
                <i class="fas fa-paper-plane mr-2"></i>Submit Transfer
              </button>
            </div>
          </section>
        </form>

        <!-- Pending transfers -->
        <section class="mt-10" v-if="transfers.length">
          <h2 class="text-lg font-semibold text-accent mb-4">Pending Transfers</h2>
          <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-700 text-xs uppercase tracking-wide">
                <tr>
                  <th class="px-5 py-3 text-left">Date</th>
                  <th class="px-5 py-3 text-left">Project</th>
                  <th class="px-5 py-3 text-right">Amount</th>
                  <th class="px-5 py-3 text-left">Recipient</th>
                  <th class="px-5 py-3 text-left">Status</th>
                  <th class="px-5 py-3 text-left">Reference</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="t in transfers" :key="t.ref" class="hover:bg-gray-50">
                  <td class="px-5 py-3 text-gray-700">{{ t.date }}</td>
                  <td class="px-5 py-3 text-gray-700">{{ t.project }}</td>
                  <td class="px-5 py-3 text-right font-medium text-gray-700">{{ t.amount }}</td>
                  <td class="px-5 py-3 text-gray-600">{{ t.recipient }}</td>
                  <td class="px-5 py-3">
                    <span class="bg-yellow-100 text-yellow-800 inline-flex px-2 py-0.5 rounded-full text-xs font-medium">{{ t.status }}</span>
                  </td>
                  <td class="px-5 py-3 text-xs text-gray-500">{{ t.ref }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
